import * as Enumerable from 'linq';
import { AddressUtility } from './address.utility';
import { PriorityUtility } from './priority.utility';
import { WorkOrderUtility } from './workorder.utility';
import { WorkOrderUpdateResponse } from '../responses/job/workorder.update.response';
import { SystemWorkOrderStatus } from '../entities/job/system.workorder.status';
import { WorkOrder } from '../entities/job/workorder';
import { WorkOrderStatus } from '../entities/job/workorder.status';
import { FilterFieldName, JobViewFilters } from '../entities/job/job.filters';
import { StatusFilterValue } from '../entities/job/job.filter.types';
import { Address } from '../entities/shared/address';
import { Job, JobDateSort, JobSorting } from '../entities/job/job';
import { JobSource } from '../entities/job/job.source';
import { DefaultView, JobViewSummary } from '../entities/job/job.view';
import { JobDisplay } from '../entities/job/job.displays';
import { JobGroupBy } from '../entities/job/job.group.by';
import { JobViewType } from '../entities/job/job.view.type';
import { SortJobBy } from '../entities/job/job.sort.options';
import { WorkOrderStatusSystemName } from '../entities/job/workorder.status.system.name';
import { Priority } from '../entities/tenant/priority';
import { DateUtility } from './date.utility';
import { SitePro } from '../entities/crm/sites/site.pro';
import { SiteTiny } from '../entities/shared/site';

export interface JobProcessingResponse {
  jobRes: {
    jobId: string;
    workOrderId: string;
    status: SystemWorkOrderStatus;
    workOrders?: WorkOrder[];
    completedDate?: Date;
    completedNote?: string;
    finalisedDate?: Date;
  };
  workorderRes: WorkOrderUpdateResponse[];
}

export interface RequestStatus {
  status: WorkOrderStatus;
  openStatus: WorkOrderStatus;
}

export interface WorkOrderLike {
  _id: string;
  status: WorkOrderStatus;
}

export class JobUtility {
  public static allowAutomaticUpdate(wo: WorkOrder) {
    if (wo.manuallyUpdated) return false;
    if (wo.status && (wo.status.systemStatus === 'Completed' || wo.status.systemStatus === 'Closed')) return false;

    return true;
  }

  public static buildJobStatus(workOrders: WorkOrderLike[]): SystemWorkOrderStatus {
    const wos = Enumerable.from(workOrders).where((e: WorkOrderLike) => e._id !== '0');

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Closed;

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusFinalised(p.status) || WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Finalised;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusOnHold(p.status))) return SystemWorkOrderStatus.OnHold;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusInProgress(p.status))) return SystemWorkOrderStatus.InProgress;

    if (wos.any((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusOpen(p.status))) return SystemWorkOrderStatus.Open;

    if (wos.all((p: WorkOrderLike) => WorkOrderUtility.isSystemStatusCompleted(p.status) || WorkOrderUtility.isSystemStatusFinalised(p.status) || WorkOrderUtility.isSystemStatusClosed(p.status))) return SystemWorkOrderStatus.Completed;

    return SystemWorkOrderStatus.Open;
  }

  public static enableFilterField(fieldName: FilterFieldName, filters: JobViewFilters) {
    if (!filters) return false;

    switch (fieldName) {
      case FilterFieldName.Customer: {
        return !!filters.customers?.length;
      }
      case FilterFieldName.BillingCompanies: {
        return !!filters.billingCompanies?.length;
      }
      case FilterFieldName.Status: {
        return !!filters.statuses?.length;
      }
      case FilterFieldName.JobType: {
        return !!filters.jobTypes?.length;
      }
      case FilterFieldName.PriceBook: {
        return !!filters.priceBooks?.length;
      }
      case FilterFieldName.Priority: {
        return !!filters.priorities?.length;
      }
      case FilterFieldName.Owner: {
        return !!filters.owners?.length;
      }
      case FilterFieldName.FieldSupervisor: {
        return !!filters.fieldSupervisors?.length;
      }
      case FilterFieldName.Technician: {
        return !!filters.technicians?.length;
      }
      case FilterFieldName.Label: {
        return !!filters.labels?.length;
      }
      case FilterFieldName.Zones: {
        return !!filters.zones?.length;
      }
      case FilterFieldName.Resources: {
        return !!filters.resources?.length;
      }
      case FilterFieldName.WorkCategories: {
        return !!filters.workCategories?.length;
      }

      case FilterFieldName.CreatedAt:
      case FilterFieldName.DueDate:
      case FilterFieldName.CompletedDate:
      case FilterFieldName.FinalisedDate: {
        return true;
      }

    }
  }

  /// Used in job.sorting.dueDate
  public static getSortableDate(date?: Date): JobDateSort {
    if (!date) {
      const sorting = {
        asc: new Date(2100, 12, 31, 0, 0),
        desc: new Date(1980, 1, 1, 0, 0),
      };
      return sorting;
    }

    const sorting = {
      asc: DateUtility.getDate(date),
      desc: DateUtility.getDate(date),
    }
    return sorting;
  }

  /// Used in job.sorting.priority
  public static getSortablePriority(priority?: Priority): number {
    const sort = PriorityUtility.getSortOrder(priority);
    return sort;
  }

  public static getSortable(priority: Priority, dueDate: Date, completedDate: Date): JobSorting {
    return {
      priority: this.getSortablePriority(priority),
      dueDate: this.getSortableDate(dueDate),
      completedDate: this.getSortableDate(completedDate)
    } as JobSorting;
  }

  public static address(address: Address): string {
    return AddressUtility.formatAddress(address);
  }

  public static getAddress1(address: Address): string {
    if (address == null) return '';

    /**
     * Cong Note:
     *  
     * Address 1 = Address Line 1 = Street2, Street1
     * Address 2 = Address Line 2 = Suburb, State, Postcode
     * 
     */

    const street1 = address.street1?.trim() ?? '';
    const street2 = address.street2?.trim() ?? '';

    const result = [street2, street1]
      .filter(x => !!x)
      .join('; ');

    return result;
  }

  public static getAddress2(address: Address): string {

    if (address == null) return '';

    const components = [];

    const postCode = address?.postcode?.toString()?.trim();
    const postalCode = address?.postalCode?.trim();
    const safePostalCode = postalCode
      ? postalCode
      : postCode;

    if (address.suburb) components.push(address?.suburb?.trim());
    // if (address.state) components.push(address?.state?.trim());
    if (address.state) components.push(address?.state?.trim());
    if (safePostalCode) components.push(safePostalCode);
    if (address.country) components.push(address.country);

    //address2 = [address.suburb ? address.suburb : '', address.state ? address.state : '', address.postcode ? address.postcode : '', address.country ? address.country : ''].join(' ').trim();
    const address2 = components.join(' ').trim();

    return address2;
  }

  public static getSiteAddress(item: SitePro | SiteTiny): string {
    const addressName = item?.address?.name?.trim();
    const addressAutoName = JobUtility.address(item.address);
    return addressName || addressAutoName;
  }

  public static convertToJobResponse(job: Job): JobProcessingResponse {
    if (!job) return null;

    const dbWorkorders: WorkOrderUpdateResponse[] = [];

    Enumerable.from(job.workOrders).forEach((w) => {
      const workorder = {
        jobId: job._id,
        workOrderId: w._id,
        status: w.status,
      } as WorkOrderUpdateResponse;

      return dbWorkorders.push(workorder);
    });

    const dbJob = {
      jobId: job._id,
      workOrderId: job._id,
      status: job.status,
      workOrders: job.workOrders,
      completedDate: job.completedDate,
      completedNote: job.completedNote,
      finalisedDate: job.finalisedDate,
      updatedAt: job?.updatedAt,
    };

    return { jobRes: dbJob, workorderRes: dbWorkorders } as JobProcessingResponse;
  }

  public static createEmptyView(name = 'Not Configured'): JobViewSummary {

    return {
      _id: DefaultView.Empty,
      name,
      isDefault: true,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: null
    } as JobViewSummary;

  }

  public static createDefaultViews(statuses: StatusFilterValue[]): JobViewSummary[] {
    const defaultViews = [];

    defaultViews.push({
      _id: DefaultView.Active,
      name: 'Active Jobs',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        groupBy: JobGroupBy.Job,
        viewType: JobViewType.WorkOrder,
        displayType: JobDisplay.Kanban,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Completed) return true;

            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Finalised) return true;
            return false;
          })
          .toArray(),
      },
    } as JobViewSummary);

    // defaultViews.push({
    //   _id: DefaultView.ActivePro,
    //   name: 'Active Jobs Pro',
    //   isDefault: false,
    //   system: true,
    //   selected: false,
    //   description: '',
    //   owner: null,
    //   source: JobSource.Active,
    //   filters: {
    //     groupBy: JobGroupBy.Job,
    //     viewType: JobViewType.WorkOrder,
    //     displayType: JobDisplay.Kanban,
    //     statuses: Enumerable.from(statuses)
    //       .where((e) => {
    //         if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Completed) return true;

    //         if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Finalised) return true;
    //         return false;
    //       })
    //       .toArray(),
    //   },
    // } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Overdue,
      name: 'Overdue',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        overDueDate: new Date(),
        groupBy: JobGroupBy.Job,
        viewType: JobViewType.WorkOrder,
        displayType: JobDisplay.Kanban,
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            return false;
          })
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.DueIn7Days,
      name: 'Due In Next 7 Days',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        dueBeforeDate: DateUtility.getDateTime(new Date()).plus({ days: 7 }).endOf('day').toJSDate(),
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.Kanban,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => {
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.Open) return true;
            if (!e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.InProgress) return true;
            if (e.isSystemStatus && e.systemStatus === SystemWorkOrderStatus.OnHold) return true;
            return false;
          })
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Unassigned,
      name: 'Not Assigned',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      filters: {
        unassigned: true,
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.List,
        sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
        statuses: Enumerable.from(statuses)
          .where((e) => e.systemName === WorkOrderStatusSystemName.New && !e.isSystemStatus)
          .toArray(),
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.NotDispatched,
      name: 'Not Dispatched',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Active,
      sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
      filters: {
        statuses: Enumerable.from(statuses)
          .where((e) => e.systemName === WorkOrderStatusSystemName.Assigned)
          .toArray(),
        viewType: JobViewType.WorkOrder,
        groupBy: JobGroupBy.Job,
        displayType: JobDisplay.List,
      } as JobViewFilters,
    } as JobViewSummary);

    defaultViews.push({
      _id: DefaultView.Closed,
      name: 'Closed Jobs',
      isDefault: false,
      system: true,
      selected: false,
      description: '',
      owner: null,
      source: JobSource.Closed,
      sort: { sortBy: SortJobBy.CreatedDate, sortType: 'DESC' },
      filters: {
        displayType: JobDisplay.List,
        viewType: JobViewType.Job,
        statuses: Enumerable.from(statuses)
          .where((e) => (e.systemStatus === SystemWorkOrderStatus.Closed || e.systemStatus === SystemWorkOrderStatus.Canceled) && !e.isSystemStatus)
          .toArray(),
      },
    } as JobViewSummary);

    return defaultViews;
  }
}
